/**
 * The Toolbox tab: scriptable buttons for the open snapshot.
 *
 * The buttons come from a toolbox file in the flow's `snapshots/toolboxScripts`
 * folder, bound to the snapshot the way a design is — `metadata.toolbox`, or
 * failing that the snapshot's own base name (server: toolboxFile.js). The file
 * runs in a Web Worker (modules/snapshotToolbox/runner.js) and reaches the
 * editor only through the `ctx` handlers built here, so every edit it makes goes
 * through `editor.applySnapshotChange` like any panel's: undoable, debounced,
 * conflict-merged, refused on a locked snapshot.
 *
 * Kept mounted and unfrozen across tab switches (see containerSnapshotConfig):
 * a run that is waiting on a pick list must survive a look at another tab.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  IconButton,
  List,
  ListSubheader,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import StopIcon from "@material-ui/icons/Stop";
import { sendCommand } from "../../modules/playEngineBridge";
import { genAssetKey, localFileToBase64 } from "../../modules/playEngineAssetUtils";
import {
  LIBRARY_CHANGED_EVENT,
  SNAPSHOT_IFRAME_ID,
  createAssetFolder,
  discardAssets,
  getSnapshotToolbox,
  libraryFiles,
  libraryTree,
  listSnapshotToolboxes,
  uploadAssetToLibrary,
} from "../../modules/snapshotEditorApi";
import { albert, getLevel, listLevels, repoFiles, repoTree } from "../../modules/levelApi";
import { optimizeSourceForSnapshot } from "../../modules/snapshotOptimize";
import { applyToolboxPatches, startToolboxRunner } from "../../modules/snapshotToolbox/runner";
import { BAR_BG, DARK_SCROLLBAR_CSS, FIELD_LABEL, PANEL_BG, SECTION_DIVIDER } from "./panelPalette";

const AUTOMATIC = "";

/** A row the script marks as already dealt with — a game that has a playable. */
function itemDone(item) {
  return Boolean(item && typeof item === "object" && item.done);
}

/**
 * What a filter matches against: everything the row shows plus what it is
 * grouped under, so typing a studio's name finds its games.
 */
function searchText(item) {
  if (item == null || typeof item !== "object") return pickItemLabel(item);
  return [pickItemLabel(item), item.group, item.description, item.path]
    .filter(Boolean)
    .join(" ");
}

/** The heading a row sits under, when the script groups its items. */
function itemGroup(item) {
  return item && typeof item === "object" && item.group ? String(item.group) : null;
}

/** What a pick-list row shows: scripts hand over plain data, not markup. */
function pickItemLabel(item) {
  if (item == null) return "";
  if (typeof item !== "object") return String(item);
  return String(item.label || item.title || item.name || item.id || JSON.stringify(item));
}

export default function ContainerSnapshotToolbox({ editor, userPermissions, createSnackbar }) {
  // The same two spellings the other panels read (see containerSnapshotConfig).
  const canEdit =
    _.get(userPermissions, "editContent") !== false &&
    _.get(userPermissions, "update") !== false &&
    !editor.isLocked;
  const fileName = editor.loadedFileName;
  const boundToolboxId = _.get(editor.snapshot, "metadata.toolbox") || AUTOMATIC;

  const [toolboxes, setToolboxes] = useState([]);
  const [file, setFile] = useState({ toolboxId: null, source: null });
  const [fileError, setFileError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState(null); // { title, buttons }
  const [loadError, setLoadError] = useState(null);
  const [runningButtonId, setRunningButtonId] = useState(null);
  const [pickRequest, setPickRequest] = useState(null);
  const [pickFilter, setPickFilter] = useState("");
  const [toolboxFilter, setToolboxFilter] = useState("");
  // Per list of the toolbox: what it loaded, what is being typed into its filter
  // and which of its rows is running. Keyed by list id.
  const [listState, setListState] = useState({});
  const [openList, setOpenList] = useState(null);

  // The handlers answer the worker long after the render that made them, so
  // they read the editor through a ref rather than a stale closure.
  const editorRef = useRef(editor);
  editorRef.current = editor;

  // What the script's edits build on. It follows the editor, and runs ahead of
  // it within a run: two applyChange calls in a row must not both diff against
  // the render before the first.
  const workingSnapshotRef = useRef(editor.snapshot);
  const lastSeenSnapshotRef = useRef(editor.snapshot);
  if (lastSeenSnapshotRef.current !== editor.snapshot) {
    lastSeenSnapshotRef.current = editor.snapshot;
    workingSnapshotRef.current = editor.snapshot;
  }

  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  // home.js hands a fresh createSnackbar on most renders; the handlers must not
  // change with it, or every render would restart the worker.
  const createSnackbarRef = useRef(createSnackbar);
  createSnackbarRef.current = createSnackbar;

  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // ── Which toolbox, and its source ──────────────────────────────────────────

  useEffect(() => {
    if (!editor.baseID) return undefined;
    let cancelled = false;
    listSnapshotToolboxes(editor.baseID).then((response) => {
      if (cancelled) return;
      setToolboxes(response.ok ? _.get(response, "data.toolboxes", []) : []);
    });
    return () => {
      cancelled = true;
    };
  }, [editor.baseID, reloadToken]);

  useEffect(() => {
    if (!editor.baseID || !fileName) {
      setFile({ toolboxId: null, source: null });
      return undefined;
    }
    let cancelled = false;
    setIsFetching(true);
    setFileError(null);
    getSnapshotToolbox(editor.baseID, fileName)
      .then((response) => {
        if (cancelled) return;
        if (!response.ok) {
          const code = _.get(response, "error.error") || response.error;
          setFile({ toolboxId: _.get(response, "error.toolboxId", null), source: null });
          setFileError(typeof code === "string" ? code : "TOOLBOX_LOOKUP_FAILED");
          return;
        }
        setFile({ toolboxId: response.data.toolboxId, source: response.data.source });
      })
      .catch((error) => {
        if (!cancelled) setFileError(error.message);
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
    // The binding is a dependency so undo/redo of it re-reads too.
  }, [editor.baseID, fileName, boundToolboxId, reloadToken]);

  const handleBind = useCallback(
    async (toolboxId) => {
      editor.setSnapshotPathValue("metadata.toolbox", toolboxId || null);
      // Flushed rather than debounced: the server resolves the binding from
      // the file on disk, so re-reading before the save would get the old one.
      await editor.flushSave();
      setReloadToken((token) => token + 1);
    },
    [editor]
  );

  // ── ctx handlers ───────────────────────────────────────────────────────────

  const runFileRef = useRef(null);
  const pickResolveRef = useRef(null);

  /** Settle the open pick list, if any: the chosen item, or null for cancel. */
  const closePick = useCallback((value) => {
    const resolve = pickResolveRef.current;
    pickResolveRef.current = null;
    if (resolve) resolve(value);
    if (isMountedRef.current) setPickRequest(null);
  }, []);

  // Everything below reads refs, so the handlers are made once: a new handlers
  // object would mean a new worker.
  const handlers = useMemo(() => {
    const assertSameSnapshot = () => {
      if (editorRef.current.loadedFileName !== runFileRef.current) {
        throw new Error("Another snapshot was opened while the button was running.");
      }
    };
    /** A jobsApi response, as the value a toolbox script asked for. */
    const unwrap = (response, key) => {
      if (!response || !response.ok) {
        throw new Error(String((response && response.error) || "The level source did not answer."));
      }
      // No key: the whole body, for a source whose answer is its own envelope.
      return key ? _.get(response, ["data", key]) : _.get(response, "data");
    };
    const assertCanEdit = () => {
      assertSameSnapshot();
      if (!canEditRef.current) {
        throw new Error("This snapshot cannot be edited (locked, or no edit permission).");
      }
    };

    return {
      getSnapshot: async () => {
        assertSameSnapshot();
        return workingSnapshotRef.current;
      },
      applyChange: async (patches) => {
        assertCanEdit();
        const next = applyToolboxPatches(workingSnapshotRef.current, patches);
        workingSnapshotRef.current = next;
        editorRef.current.applySnapshotChange(next);
        return true;
      },
      addAsset: async (type, source, options = {}) => {
        assertCanEdit();
        if (typeof type !== "string" || !type) throw new Error("addAsset needs an asset type.");
        if (!source) throw new Error("addAsset needs a source (a data URL).");
        const optimized = await optimizeSourceForSnapshot({
          baseID: editorRef.current.baseID,
          assetType: type,
          source,
          fileName: options.fileName,
        });
        assertCanEdit();
        const current = workingSnapshotRef.current;
        const assets = current && _.isPlainObject(current.assets) ? current.assets : {};
        const assetKey = options.key && !assets[options.key] ? options.key : genAssetKey(type, Object.keys(assets));
        const next = applyToolboxPatches(current, [
          {
            path: ["assets", assetKey],
            value: { type, bucket: "nobucket", source: optimized, sourceRef: null },
          },
        ]);
        workingSnapshotRef.current = next;
        editorRef.current.applySnapshotChange(next);
        return assetKey;
      },
      reloadPreview: async () => {
        window.dispatchEvent(new Event("playengine:agent-reload"));
        return true;
      },
      sendToPreview: async (payload, timeout) => {
        if (!payload || typeof payload.type !== "string") {
          throw new Error("sendToPreview needs a message with a `type`.");
        }
        return sendCommand(SNAPSHOT_IFRAME_ID, payload, timeout || 5000);
      },
      // `keep` leaves the message up until it is dismissed — for the one that
      // says what a button did, which a passing snackbar is too easy to miss.
      notify: async (message, variant, keep) => {
        createSnackbarRef.current?.(String(message), variant || "info", keep === true);
        return true;
      },
      // Not guarded by the snapshot: these read from the level source, touch
      // nothing here, and a list is loaded before any snapshot is in play.
      // The whole body: a level listing also says where it was read from, which
      // the panel shows above the list.
      listLevels: async (options) => unwrap(await listLevels(editorRef.current.baseID, options || {})),
      repoTree: async (project, options) => {
        if (!project) throw new Error("repoTree needs a project path.");
        return unwrap(await repoTree(editorRef.current.baseID, project, options || {}), "entries");
      },
      repoFiles: async (project, paths, options) => {
        if (!project || !Array.isArray(paths)) throw new Error("repoFiles needs a project path and a list of paths.");
        return unwrap(await repoFiles(editorRef.current.baseID, project, paths, options || {}), "files");
      },
      libraryTree: async (path, options) =>
        unwrap(await libraryTree(editorRef.current.baseID, path || "", options || {}), "entries"),
      libraryFiles: async (paths) => {
        if (!Array.isArray(paths)) throw new Error("libraryFiles needs a list of paths.");
        return unwrap(await libraryFiles(editorRef.current.baseID, paths), "files");
      },
      albert: async (options) => unwrap(await albert(editorRef.current.baseID, options || {})),
      getLevel: async (levelId, options) => {
        if (!levelId) throw new Error("getLevel needs a level id.");
        return unwrap(await getLevel(editorRef.current.baseID, levelId, options || {}), "level");
      },
      pick: async ({ title, items } = {}) => {
        assertSameSnapshot();
        if (!Array.isArray(items)) throw new Error("pick needs an `items` array.");
        closePick(null);
        return new Promise((resolve) => {
          pickResolveRef.current = resolve;
          setPickFilter("");
          setPickRequest({ title: title ? String(title) : "Choose one", items });
        });
      },
      // The same list, for a question whose answers are `{ value, label }`: it
      // settles with the value rather than the row, and with null when cancelled.
      choose: async ({ title, message, options } = {}) => {
        assertSameSnapshot();
        if (!Array.isArray(options)) throw new Error("choose needs an `options` array.");
        closePick(null);
        const chosen = await new Promise((resolve) => {
          pickResolveRef.current = resolve;
          setPickFilter("");
          setPickRequest({
            title: title ? String(title) : "Choose one",
            message: message ? String(message) : null,
            items: options,
          });
        });
        return chosen && typeof chosen === "object" && chosen.value !== undefined ? chosen.value : chosen;
      },
    };
  }, [closePick]);

  // ── The worker ─────────────────────────────────────────────────────────────

  const runnerRef = useRef(null);
  const [runnerGeneration, setRunnerGeneration] = useState(0);

  // One worker per toolbox source and snapshot. Opening another snapshot or
  // changing the file kills whatever was running against the old one.
  useEffect(() => {
    setLoaded(null);
    setLoadError(null);
    setRunningButtonId(null);
    if (!file.source) return undefined;

    const runner = startToolboxRunner({ source: file.source, handlers });
    runnerRef.current = runner;
    runner.loaded.then(setLoaded).catch((error) => {
      if (runnerRef.current === runner) setLoadError(error.message);
    });

    return () => {
      if (runnerRef.current === runner) runnerRef.current = null;
      runner.terminate();
      closePick(null);
    };
  }, [file.source, fileName, handlers, runnerGeneration, closePick]);

  const runButton = useCallback(
    async (button) => {
      const runner = runnerRef.current;
      if (!runner || runningButtonId) return;
      runFileRef.current = editorRef.current.loadedFileName;
      setRunningButtonId(button.id);
      try {
        await runner.run(button.id);
      } catch (error) {
        if (runnerRef.current === runner) {
          createSnackbar?.(`${button.label}: ${error.message}`, "error", false);
        }
      } finally {
        if (runnerRef.current === runner) setRunningButtonId(null);
      }
    },
    [createSnackbar, runningButtonId]
  );

  // Terminating is the only way to stop a script mid-await, so Stop starts a
  // fresh worker on the same source.
  const stop = useCallback(() => {
    setRunnerGeneration((generation) => generation + 1);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  const isBoundKnown =
    boundToolboxId === AUTOMATIC || toolboxes.some((entry) => entry.toolboxId === boundToolboxId);

  // A flow accumulates toolboxes, so the list is searched as well as scrolled.
  // The bound one always stays in it: a filter must not hide what is selected.
  const filteredToolboxes = useMemo(() => {
    const query = toolboxFilter.trim().toLowerCase();
    if (!query) return toolboxes;
    return toolboxes.filter(
      (entry) =>
        entry.toolboxId === boundToolboxId ||
        `${entry.title || ""} ${entry.toolboxId}`.toLowerCase().includes(query)
    );
  }, [boundToolboxId, toolboxFilter, toolboxes]);

  const filteredPickItems = useMemo(() => {
    if (!pickRequest) return [];
    const query = pickFilter.trim().toLowerCase();
    const indexed = pickRequest.items.map((item, index) => ({ item, index }));
    if (!query) return indexed;
    return indexed.filter(({ item }) => searchText(item).toLowerCase().includes(query));
  }, [pickFilter, pickRequest]);

  const patchList = useCallback((listId, patch) => {
    setListState((state) => ({ ...state, [listId]: { ...state[listId], ...patch } }));
  }, []);

  const loadList = useCallback(
    async (listId, { refresh = false } = {}) => {
      const runner = runnerRef.current;
      if (!runner) return;
      runFileRef.current = editorRef.current.loadedFileName;
      patchList(listId, { isLoading: true, error: null });
      try {
        const result = await runner.loadList(listId, { refresh });
        // A list may answer with its rows alone, or with `{ items, source }` —
        // where the rows came from, which only the script knows and only once it
        // has read the snapshot.
        const items = Array.isArray(result) ? result : _.get(result, "items") || [];
        const source = Array.isArray(result) ? null : _.get(result, "source") || null;
        if (isMountedRef.current) patchList(listId, { items, source, isLoading: false });
      } catch (error) {
        // A worker replaced mid-fetch rejects what it was carrying. Its
        // successor loads every list as it starts, so that is a handover, not
        // a failure to put in front of the user.
        if (runnerRef.current !== runner) return;
        if (isMountedRef.current) patchList(listId, { isLoading: false, error: error.message });
      }
    },
    [patchList]
  );

  // Loaded once, when the toolbox is. The point of a list is that it is already
  // there — a press should apply a choice, not fetch one.
  useEffect(() => {
    if (!loaded) return;
    loaded.lists.forEach((list) => loadList(list.id));
  }, [loadList, loaded, runnerGeneration]);

  // A list built from the library goes stale as soon as anything writes to it —
  // a folder discarded in the Assets tab's browser leaves a row pointing at
  // nothing. Settled first, because one upload is one call per file and the
  // list should be fetched after the last of them, not after each.
  useEffect(() => {
    if (!loaded) return undefined;
    const libraryLists = loaded.lists.filter((list) => list.upload);
    if (!libraryLists.length) return undefined;

    let timer = null;
    const onLibraryChange = () => {
      clearTimeout(timer);
      timer = setTimeout(() => libraryLists.forEach((list) => loadList(list.id, { refresh: true })), 600);
    };
    window.addEventListener(LIBRARY_CHANGED_EVENT, onLibraryChange);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(LIBRARY_CHANGED_EVENT, onLibraryChange);
    };
  }, [loadList, loaded]);

  const selectFromList = useCallback(
    async (listId, index, label) => {
      const runner = runnerRef.current;
      if (!runner) return;
      runFileRef.current = editorRef.current.loadedFileName;
      workingSnapshotRef.current = editorRef.current.snapshot;
      patchList(listId, { busyIndex: index, error: null });
      try {
        await runner.select(listId, index);
      } catch (error) {
        if (runnerRef.current === runner) createSnackbar?.(`${label}: ${error.message}`, "error", false);
      } finally {
        if (isMountedRef.current) patchList(listId, { busyIndex: null });
      }
    },
    [createSnackbar, patchList]
  );

  // A list whose script declares `upload` takes a whole folder from the machine
  // into the library — the folder's own name is the entry's name, so nothing is
  // renamed on the way in and the script finds what it expects. An existing
  // folder is emptied first: the upload endpoint renames rather than overwrites,
  // and a level with two `objects.json` in it is no level.
  const uploadFolderToList = useCallback(
    async (list, fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      const libraryPath = String(list.upload.libraryPath || "").replace(/\/+$/, "");
      // The server keeps only these characters in a name it is given, so the
      // folder it makes is not always the one that was picked — and the files
      // have to go into the folder it actually made.
      const folderName = String(files[0].webkitRelativePath || files[0].name)
        .split("/")[0]
        .replace(/[^A-Za-z0-9._-]/g, "_");
      if (!folderName || folderName.startsWith(".")) return;

      const accepted = (list.upload.accept || "")
        .split(",")
        .map((extension) => extension.trim().toLowerCase())
        .filter(Boolean);
      const wanted = files.filter((file) => {
        if (file.name.startsWith(".")) return false;
        if (!accepted.length) return true;
        return accepted.some((extension) => file.name.toLowerCase().endsWith(extension));
      });
      if (!wanted.length) {
        createSnackbar?.(`${folderName}: nothing to upload.`, "warning", false);
        return;
      }

      // postJson reports a refusal rather than throwing one, and a half-written
      // folder is worse than none: every step is checked.
      const ensureOk = (response) => {
        if (!response || !response.ok) {
          throw new Error(String((response && response.error) || "The library did not answer."));
        }
        return response.data;
      };

      patchList(list.id, { isUploading: true, error: null });
      try {
        const siblings = ensureOk(await libraryTree(editorRef.current.baseID, libraryPath)).entries || [];
        const existing = siblings.find((entry) => entry.type === "tree" && entry.name === folderName);
        if (existing) {
          if (!window.confirm(`Replace ${folderName}? Everything in it will be removed first.`)) return;
          ensureOk(
            await discardAssets(editorRef.current.baseID, {
              paths: [existing.path],
              expect: "dir",
              recursive: true,
            })
          );
        }
        const made = ensureOk(
          await createAssetFolder(editorRef.current.baseID, { dirPath: libraryPath, name: folderName })
        );
        const folderPath = made.relativePath;

        for (const file of wanted) {
          ensureOk(
            await uploadAssetToLibrary(editorRef.current.baseID, {
              dirPath: folderPath,
              fileName: file.name,
              source: await localFileToBase64(file),
            })
          );
        }

        const runner = runnerRef.current;
        if (runner) {
          await runner.uploaded(list.id, { folder: folderPath, paths: wanted.map((file) => file.name) });
        }
        // No reload here: every upload announced the change, and the listener
        // above fetches the list once the burst has settled.
      } catch (error) {
        createSnackbar?.(`${folderName}: ${error.message}`, "error", false);
        if (isMountedRef.current) patchList(list.id, { error: error.message });
      } finally {
        if (isMountedRef.current) patchList(list.id, { isUploading: false });
      }
    },
    [createSnackbar, patchList]
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: PANEL_BG,
      }}
    >
      <style>{DARK_SCROLLBAR_CSS}</style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 10px",
          backgroundColor: BAR_BG,
          borderBottom: `1px solid ${SECTION_DIVIDER}`,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8", flex: 1, minWidth: 0 }}>
          {(loaded && loaded.title) || file.toolboxId || "Toolbox"}
        </span>
        {/* The picker is for swapping toolboxes, which is rare — it names the
            file rather than repeating the title the panel already shows. */}
        {/* Which toolbox this snapshot uses. Like a design, a toolbox is a
            file shared by every snapshot of a template — so this picks a file,
            it does not edit one. */}
        <Select
          displayEmpty
          value={isBoundKnown ? boundToolboxId : AUTOMATIC}
          disabled={!canEdit || isFetching || Boolean(runningButtonId)}
          onChange={(event) => handleBind(event.target.value || null)}
          onClose={() => setToolboxFilter("")}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              className: "proto-dark-scroll",
              style: { backgroundColor: PANEL_BG, color: "#e8e8e8", maxHeight: 360 },
            },
          }}
          style={{ fontSize: 12, color: "#fff", minWidth: 150, maxWidth: 220 }}
        >
          {/* A subheader rather than a MenuItem: the field is not an option, and
              the menu's own type-ahead would steal every keystroke from it. */}
          <ListSubheader style={{ backgroundColor: PANEL_BG, padding: "6px 10px", lineHeight: 1 }}>
            <TextField
              autoFocus
              fullWidth
              placeholder="Search"
              value={toolboxFilter}
              onChange={(event) => setToolboxFilter(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              InputProps={{ style: { fontSize: 12, color: "#eee" } }}
            />
          </ListSubheader>
          <MenuItem value={AUTOMATIC} style={{ color: "#8a8d93", fontSize: 12 }}>
            None — match by file name
          </MenuItem>
          {!isBoundKnown ? (
            <MenuItem disabled style={{ fontSize: 11, color: "#c9a25b" }}>
              {boundToolboxId} (not on this flow)
            </MenuItem>
          ) : null}
          {filteredToolboxes.map((entry) => (
            <MenuItem key={entry.toolboxId} value={entry.toolboxId} style={{ color: "#eee", fontSize: 12 }}>
              {/* The toolbox's own title, not its file name: a picker that reads
                  "levels" and "sticker" says nothing about which game each serves. */}
              {entry.title || entry.toolboxId}
              {entry.isBuiltin ? " (built in)" : ""}
              {entry.isReadable === false ? " (unreadable)" : ""}
            </MenuItem>
          ))}
          {toolboxFilter.trim() && !filteredToolboxes.length ? (
            <MenuItem disabled style={{ fontSize: 12, color: "#8a8d93" }}>
              No toolbox matches
            </MenuItem>
          ) : null}
        </Select>
        <Tooltip title="Re-read toolboxes from disk">
          <span>
            <IconButton
              size="small"
              aria-label="Re-read toolboxes"
              disabled={isFetching || Boolean(runningButtonId)}
              onClick={() => setReloadToken((token) => token + 1)}
              style={{ color: "#bbb" }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      <div
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {isFetching && !file.source ? <CircularProgress size={18} /> : null}

        {fileError ? (
          <Typography style={{ fontSize: 12, color: "#e57373" }}>
            Could not read the toolbox{file.toolboxId ? ` "${file.toolboxId}"` : ""}: {fileError}
          </Typography>
        ) : null}

        {!isFetching && !fileError && !file.source ? (
          <Typography style={{ fontSize: 12, color: FIELD_LABEL, lineHeight: 1.5 }}>
            No toolbox for this snapshot. Add a <code>&lt;name&gt;.toolbox.js</code> file to the
            flow&apos;s <code>snapshots/toolboxScripts</code> folder and pick it above, or name it
            after the snapshot.
          </Typography>
        ) : null}

        {loadError ? (
          <Typography style={{ fontSize: 12, color: "#e57373", whiteSpace: "pre-wrap" }}>
            The toolbox script failed to load: {loadError}
          </Typography>
        ) : null}

        {file.source && !loaded && !loadError ? <CircularProgress size={18} /> : null}

        {loaded && loaded.buttons.length === 0 ? (
          <Typography style={{ fontSize: 12, color: FIELD_LABEL }}>This toolbox has no buttons.</Typography>
        ) : null}

        {loaded
          ? loaded.buttons.map((button) => {
            const isRunning = runningButtonId === button.id;
            const disabled = Boolean(runningButtonId) || (button.needsSnapshot && !canEdit);
            return (
              <div key={button.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Tooltip title={button.description || ""} placement="left">
                  <span style={{ flex: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={disabled}
                      onClick={() => runButton(button)}
                      startIcon={isRunning ? <CircularProgress size={14} /> : null}
                      style={{ justifyContent: "flex-start", textTransform: "none", color: "#e8e8e8" }}
                    >
                      {button.label}
                    </Button>
                  </span>
                </Tooltip>
                {isRunning ? (
                  <Tooltip title="Stop">
                    <IconButton size="small" aria-label="Stop" onClick={stop} style={{ color: "#e57373" }}>
                      <StopIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </div>
            );
          })
          : null}

        {/* The toolbox's own lists: fetched once and left on the panel, so
            choosing again is a single click. */}
        {/* More than one list reads as tabs rather than as stacked panels: the
            rail is narrow and only one of them is being worked in at a time. */}
        {loaded && loaded.lists.length > 1 ? (
          <div
            role="tablist"
            style={{ display: "flex", gap: 16, borderBottom: `1px solid ${SECTION_DIVIDER}`, marginBottom: 2 }}
          >
            {loaded.lists.map((list) => {
              const active = (openList || loaded.lists[0].id) === list.id;
              const count = (listState[list.id] || {}).items;
              return (
                <button
                  key={list.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setOpenList(list.id)}
                  style={{
                    font: "inherit",
                    fontSize: 13,
                    background: "transparent",
                    border: 0,
                    borderBottom: `2px solid ${active ? "#4db6ac" : "transparent"}`,
                    color: active ? "#e8e8e8" : FIELD_LABEL,
                    padding: "6px 0 5px",
                    cursor: "pointer",
                    display: "flex",
                    gap: 6,
                    alignItems: "baseline",
                  }}
                >
                  {list.label}
                  {count ? (
                    <span style={{ fontSize: 11, color: "#8a8d93", fontVariantNumeric: "tabular-nums" }}>
                      {count.length}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}

        {loaded
          ? loaded.lists
            .filter((list) => loaded.lists.length < 2 || (openList || loaded.lists[0].id) === list.id)
            .map((list) => {
              const state = listState[list.id] || {};
              const items = state.items || [];
              const query = (state.filter || "").trim().toLowerCase();
              const doneCount = items.filter(itemDone).length;
              const onlyDone = Boolean(state.onlyDone) && doneCount > 0;
              const shown = items
                .map((item, index) => ({ item, index }))
                .filter(({ item }) => !onlyDone || itemDone(item))
                .filter(({ item }) => !query || searchText(item).toLowerCase().includes(query));
              const busy = state.busyIndex != null || Boolean(runningButtonId);

              return (
                <div
                  key={list.id}
                  style={{
                    border: `1px solid ${SECTION_DIVIDER}`,
                    borderRadius: 4,
                    marginTop: 4,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 160,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 8px",
                      backgroundColor: BAR_BG,
                      fontSize: 12,
                      color: "#e8e8e8",
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block" }}>
                        {loaded.lists.length > 1 ? "" : list.label}
                        {items.length ? `${loaded.lists.length > 1 ? "" : " "}(${items.length})` : ""}
                      </span>
                      {state.source ? (
                        <span
                          title={state.source}
                          style={{
                            display: "block",
                            fontSize: 11,
                            color: "#8a8d93",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {state.source}
                        </span>
                      ) : null}
                    </span>
                    {state.isLoading || state.isUploading ? <CircularProgress size={14} /> : null}
                    {list.upload ? (
                      <Tooltip title={`Upload a folder into ${list.upload.libraryPath || "the library"}`}>
                        <span>
                          <Button
                            size="small"
                            component="label"
                            disabled={state.isUploading || busy}
                            style={{ color: "#bbb", textTransform: "none", fontSize: 11, minWidth: 0 }}
                          >
                            {list.upload.label}
                            <input
                              hidden
                              type="file"
                              multiple
                              {...(list.upload.folder ? { webkitdirectory: "", directory: "" } : {})}
                              {...(list.upload.accept ? { accept: list.upload.accept } : {})}
                              onChange={(event) => {
                                // Copied out before the input is cleared:
                                // `files` is live, and resetting the value
                                // empties it under a reference still held.
                                const chosen = Array.from(event.target.files || []);
                                event.target.value = "";
                                void uploadFolderToList(list, chosen);
                              }}
                            />
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Fetch this list again from the source">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Reload ${list.label}`}
                          disabled={state.isLoading || busy}
                          onClick={() => loadList(list.id, { refresh: true })}
                          style={{ color: "#bbb" }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>

                  {state.error ? (
                    <Typography style={{ fontSize: 12, color: "#e57373", padding: "6px 8px" }}>
                      {state.error}
                    </Typography>
                  ) : null}

                  {doneCount > 0 ? (
                    <div style={{ padding: "6px 8px", borderBottom: `1px solid ${SECTION_DIVIDER}` }}>
                      <button
                        type="button"
                        aria-pressed={onlyDone}
                        onClick={() => patchList(list.id, { onlyDone: !state.onlyDone })}
                        style={{
                          font: "inherit",
                          fontSize: 11.5,
                          padding: "3px 10px",
                          borderRadius: 999,
                          cursor: "pointer",
                          color: onlyDone ? "#e8e8e8" : FIELD_LABEL,
                          background: onlyDone ? "#1f3b39" : "transparent",
                          border: `1px solid ${onlyDone ? "#2f6f69" : SECTION_DIVIDER}`,
                        }}
                      >
                      Converted · {doneCount}
                      </button>
                    </div>
                  ) : null}

                  {items.length > 8 ? (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Filter…"
                      value={state.filter || ""}
                      onChange={(event) => patchList(list.id, { filter: event.target.value })}
                      inputProps={{ style: { fontSize: 12, color: "#e8e8e8", padding: "6px 8px" } }}
                    />
                  ) : null}

                  <List dense className="proto-dark-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {shown.map(({ item, index }, position) => [
                      itemGroup(item) && itemGroup(item) !== itemGroup((shown[position - 1] || {}).item) ? (
                        <ListSubheader
                          key={`g-${itemGroup(item)}`}
                          disableSticky
                          style={{
                            padding: "10px 10px 4px",
                            lineHeight: 1.4,
                            fontSize: 11,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "#8a8d93",
                            background: PANEL_BG,
                          }}
                        >
                          {itemGroup(item)}
                        </ListSubheader>
                      ) : null,
                      <ListItem
                        key={index}
                        button
                        disabled={busy || !canEdit}
                        onClick={() => selectFromList(list.id, index, list.label)}
                      >
                        <ListItemText
                          primary={pickItemLabel(item)}
                          secondary={
                            item && typeof item === "object" && item.description ? String(item.description) : null
                          }
                          primaryTypographyProps={{ style: { fontSize: 12, color: "#e8e8e8" } }}
                          secondaryTypographyProps={{ style: { fontSize: 11, color: FIELD_LABEL } }}
                        />
                        {state.busyIndex === index ? (
                          <CircularProgress size={14} />
                        ) : item && item.action ? (
                          <span
                            style={{
                              fontSize: 11,
                              padding: "2px 8px",
                              borderRadius: 3,
                              color: "#4db6ac",
                              border: "1px solid #2f6f69",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.action}
                          </span>
                        ) : null}
                      </ListItem>,
                    ])}
                    {!shown.length && !state.isLoading ? (
                      <ListItem>
                        <ListItemText
                          primary={items.length ? "Nothing matches." : "Nothing to choose from."}
                          primaryTypographyProps={{ style: { fontSize: 12, color: FIELD_LABEL } }}
                        />
                      </ListItem>
                    ) : null}
                  </List>
                </div>
              );
            })
          : null}

        {/* A script asked the user to choose (ctx.pick). Drawn in the tab, not a
            dialog, so it stays put while the preview is being looked at. */}
        {pickRequest ? (
          <div style={{ border: `1px solid ${SECTION_DIVIDER}`, borderRadius: 4, marginTop: 4 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 8px",
                backgroundColor: BAR_BG,
                fontSize: 12,
                color: "#e8e8e8",
              }}
            >
              <span style={{ flex: 1 }}>{pickRequest.title}</span>
              <Button size="small" onClick={() => closePick(null)} style={{ color: "#bbb", textTransform: "none" }}>
                Cancel
              </Button>
            </div>
            {pickRequest.message ? (
              <div style={{ padding: "6px 8px", fontSize: 11, color: FIELD_LABEL }}>{pickRequest.message}</div>
            ) : null}
            {pickRequest.items.length > 8 ? (
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Filter…"
                value={pickFilter}
                onChange={(event) => setPickFilter(event.target.value)}
                inputProps={{ style: { fontSize: 12, color: "#e8e8e8", padding: "6px 8px" } }}
              />
            ) : null}
            <List dense className="proto-dark-scroll" style={{ maxHeight: 320, overflowY: "auto" }}>
              {filteredPickItems.map(({ item, index }) => (
                <ListItem key={index} button onClick={() => closePick(item)}>
                  <ListItemText
                    primary={pickItemLabel(item)}
                    secondary={item && typeof item === "object" && item.description ? String(item.description) : null}
                    primaryTypographyProps={{ style: { fontSize: 12, color: "#e8e8e8" } }}
                    secondaryTypographyProps={{ style: { fontSize: 11, color: FIELD_LABEL } }}
                  />
                </ListItem>
              ))}
              {filteredPickItems.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="Nothing to choose from."
                    primaryTypographyProps={{ style: { fontSize: 12, color: FIELD_LABEL } }}
                  />
                </ListItem>
              ) : null}
            </List>
          </div>
        ) : null}
      </div>
    </div>
  );
}
